/**
 * Client-side validation for the exam IP filter.
 *
 * Mirrors `Cadmus.Verification.IpFilter` in pantheon, which is the authority:
 * this exists so a teacher sees a bad entry as they type rather than as a
 * failed mutation. Keep the accepted formats and the messages in step with it.
 */

const MAX_PREFIX_LENGTH = 32;

const BLANK_MESSAGE = "Add at least one IP address or range";
const INVALID_MESSAGE = "Not a valid IP address or range";

// No leading zeros, matching Erlang's strict IPv4 parser, which reads
// "192.168.001.1" as invalid rather than as 192.168.1.1.
const OCTET = /^(0|[1-9]\d{0,2})$/;
const DIGITS = /^\d+$/;

function isIpv4(address: string): boolean {
  const octets = address.split(".");

  return (
    octets.length === 4 &&
    octets.every((octet) => OCTET.test(octet) && Number(octet) <= 255)
  );
}

function isMask(mask: string): boolean {
  if (DIGITS.test(mask)) return Number(mask) <= MAX_PREFIX_LENGTH;
  return isIpv4(mask);
}

function isEntry(entry: string): boolean {
  const separator = entry.indexOf("/");
  if (separator === -1) return isIpv4(entry);

  return (
    isIpv4(entry.slice(0, separator)) && isMask(entry.slice(separator + 1))
  );
}

/** The entries of a filter, with whitespace and empty entries dropped. */
export function ipFilterEntries(filter: string | null | undefined): string[] {
  return (filter ?? "")
    .split(",")
    .map((entry) => entry.trim())
    .filter((entry) => entry !== "");
}

/**
 * An error message for a filter, or `null` when it is usable.
 *
 * A filter is checked whenever it holds anything, so a malformed one can't be
 * saved behind a disabled toggle; `required` additionally rejects a blank
 * filter, for when the restriction is switched on.
 */
export function ipFilterError(
  filter: string | null | undefined,
  { required }: { required: boolean }
): string | null {
  const entries = ipFilterEntries(filter);

  if (entries.length === 0) return required ? BLANK_MESSAGE : null;

  const invalid = entries.filter((entry) => !isEntry(entry));
  if (invalid.length === 0) return null;

  return `${INVALID_MESSAGE}: ${invalid.join(", ")}`;
}
